import { useState } from "react";
import axios from "axios";
import { Loader2, LockKeyhole, LogIn } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiError(detail) {
  if (!detail) return "Couldn't sign you in. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || String(detail);
}

export function AdminLogin({ onLogin, initialError = "" }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(initialError);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/reels";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-background grid place-items-center px-5 py-16">
      <div className="w-full max-w-md rounded-[1.5rem] border border-border bg-card p-7 sm:p-9 shadow-[0_30px_70px_-40px_rgba(94,39,65,0.35)]" data-testid="admin-login-card">
        <div className="w-12 h-12 rounded-full bg-accent/12 text-accent grid place-items-center">
          <LockKeyhole size={21} />
        </div>
        <p className="overline text-accent mt-6">Private clinic access</p>
        <h1 className="font-serif text-primary text-4xl tracking-tight leading-[1.05] mt-3">Reels admin</h1>
        <p className="text-sm text-muted-foreground leading-relaxed mt-4">
          Sign in with the approved clinic account to manage the Instagram reels shown on the homepage.
        </p>

        {error && (
          <div className="mt-5 rounded-xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="admin-auth-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="mt-7 space-y-4" data-testid="admin-login-form">
          <div className="space-y-2">
            <Label htmlFor="admin-email">Email</Label>
            <Input id="admin-email" type="email" data-testid="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="admin-password">Password</Label>
            <Input id="admin-password" type="password" data-testid="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
          </div>
          <Button type="submit" disabled={busy} data-testid="admin-login-submit" className="w-full rounded-full py-6 text-sm font-bold bg-accent text-accent-foreground hover:bg-accent/90">
            {busy ? <Loader2 size={16} className="mr-2 animate-spin" /> : <LogIn size={16} className="mr-2" />}
            Sign in securely
          </Button>
        </form>

        <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or <span className="h-px flex-1 bg-border" />
        </div>
        <Button type="button" variant="outline" onClick={googleLogin} data-testid="admin-google-login" className="w-full rounded-full py-6 text-sm font-bold">
          Continue with Google
        </Button>
      </div>
    </div>
  );
}
